import { CSSProperties } from "react";
import { IMacaronTemplate } from "./MacronBadge.interface";

// Utility function for hex to RGB conversion
const hexToRgb = (hex: string) => {
  // Remove # if present
  hex = hex.replace(/^#/, "");

  // Parse the hex values
  const bigint = parseInt(hex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return { r, g, b };
};

interface UseMacaronBadgeLogicParams {
  color: string;
  textColor?: string;
  template?: IMacaronTemplate;
}

export interface UseMacaronBadgeLogicResult {
  cardStyle: {
    backgroundColor: string;
    borderColor: string;
    borderWidth?: CSSProperties["borderWidth"];
    borderStyle?: CSSProperties["borderStyle"];
  };
  badgeStyle: {
    backgroundColor: string;
    color: string;
  };
}

/**
 * Hook to handle MacaronBadge component logic
 */
export const useMacaronBadgeLogic = ({
  color,
  textColor = "#ffffff",
  template = "classic",
}: UseMacaronBadgeLogicParams): UseMacaronBadgeLogicResult => {
  // Ensure color formats are correct
  let bgColor = color.startsWith("#") ? color : `#${color}`;
  const txtColor = textColor?.startsWith("#")
    ? textColor
    : `#${textColor || "ffffff"}`;
  let borderWidth: CSSProperties["borderWidth"] = "0px";
  let borderStyle: CSSProperties["borderStyle"] = "none";
  let borderColor: string = "transparent";

  // For color_and_background template, we need rgba background
  let bgColorRgba: string | undefined = "transparent";
  if (template === "color_and_background") {
    const rgb = hexToRgb(bgColor);
    bgColorRgba = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.1)`;
  }

  // For color_and_border template, we need a solid border
  if (template === "color_and_border") {
    bgColor = bgColor;
    borderColor = bgColor;
    borderWidth = "1px";
    borderStyle = "solid";
  }

  return {
    cardStyle: {
      backgroundColor: bgColorRgba,
      borderColor,
      borderWidth,
      borderStyle,
    },
    badgeStyle: {
      backgroundColor: bgColor,
      color: txtColor,
    },
  };
};
